import React, { useState } from 'react';
import { FinancialChart } from './Chart';
import { ChartData, ChartOptions } from 'chart.js';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, ArrowDown, Info, BarChart, PieChart, LineChart, Table as TableIcon, Search, ArrowUpDown } from 'lucide-react';
import { Tooltip } from './Tooltip';

interface BudgetChartProps {
  monthlyTakeHome: number;
  needsExpenses: number;
  wantsExpenses: number;
  savingsExpenses: number;
  totalTaxes: {
    federal: number;
    state: number;
    socialSecurity: number;
    medicare: number;
  };
  annualIncome: number;
}

type ViewType = 'table' | 'bar' | 'pie' | 'line';
type SortDirection = 'asc' | 'desc';
type SortField = 'category' | 'allocated' | 'actual' | 'difference' | 'status';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export const BudgetChart: React.FC<BudgetChartProps> = ({
  monthlyTakeHome,
  needsExpenses,
  wantsExpenses,
  savingsExpenses,
  totalTaxes,
  annualIncome
}) => {
  const [viewType, setViewType] = useState<ViewType>('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'category', direction: 'asc' });

  // Calculate budget allocations based on take-home pay
  const needs = monthlyTakeHome * 0.5;
  const wants = monthlyTakeHome * 0.3;
  const savings = monthlyTakeHome * 0.2;

  // Calculate tax percentages of annual income
  const taxPercentages = {
    federal: (totalTaxes.federal / annualIncome) * 100,
    state: (totalTaxes.state / annualIncome) * 100,
    socialSecurity: (totalTaxes.socialSecurity / annualIncome) * 100,
    medicare: (totalTaxes.medicare / annualIncome) * 100,
    takeHome: (monthlyTakeHome * 12 / annualIncome) * 100
  };

  const budgetData: ChartData<'bar'> = {
    labels: ['Needs (50%)', 'Wants (30%)', 'Savings (20%)'],
    datasets: [
      {
        label: 'Budget Allocation',
        data: [needs, wants, savings],
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: '#10b981',
        borderWidth: 1
      },
      {
        label: 'Current Expenses',
        data: [needsExpenses, wantsExpenses, savingsExpenses],
        backgroundColor: 'rgba(52, 211, 153, 0.5)',
        borderColor: '#34d399',
        borderWidth: 1
      }
    ]
  };

  const pieData: ChartData<'pie'> = {
    labels: [
      'Federal Tax',
      'State Tax',
      'Social Security',
      'Medicare',
      'Take-Home Pay'
    ],
    datasets: [
      {
        data: [
          totalTaxes.federal,
          totalTaxes.state,
          totalTaxes.socialSecurity,
          totalTaxes.medicare,
          monthlyTakeHome * 12
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',   // Red
          'rgba(245, 158, 11, 0.8)',  // Amber
          'rgba(59, 130, 246, 0.8)',  // Blue
          'rgba(99, 102, 241, 0.8)',  // Indigo
          'rgba(16, 185, 129, 0.8)',  // Emerald
        ],
        borderColor: '#0f172a',
        borderWidth: 2,
        hoverOffset: 20
      }
    ]
  };

  // Generate monthly trend data
  const trendData: ChartData<'line'> = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Needs',
        data: [needsExpenses, needsExpenses * 0.95, needsExpenses * 1.1, needsExpenses * 0.9, needsExpenses * 1.05, needsExpenses],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Wants',
        data: [wantsExpenses, wantsExpenses * 1.1, wantsExpenses * 0.9, wantsExpenses * 1.15, wantsExpenses * 0.85, wantsExpenses],
        borderColor: '#34d399',
        backgroundColor: 'rgba(52, 211, 153, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Savings',
        data: [savingsExpenses, savingsExpenses * 0.9, savingsExpenses * 1.2, savingsExpenses * 0.95, savingsExpenses * 1.1, savingsExpenses],
        borderColor: '#059669',
        backgroundColor: 'rgba(5, 150, 105, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const chartOptions: ChartOptions<'pie'> = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#94a3b8',
          font: {
            size: 12
          },
          padding: 20,
          usePointStyle: true
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw as number;
            const total = context.dataset.data.reduce((a, b) => (a as number) + (b as number), 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: $${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    cutout: '30%',
    radius: '90%'
  };

  const tableData = [
    {
      category: 'Needs (50%)',
      allocated: needs,
      actual: needsExpenses,
      difference: needs - needsExpenses,
      status: needsExpenses > needs ? 'over' : 'under'
    },
    {
      category: 'Wants (30%)',
      allocated: wants,
      actual: wantsExpenses,
      difference: wants - wantsExpenses,
      status: wantsExpenses > wants ? 'over' : 'under'
    },
    {
      category: 'Savings (20%)',
      allocated: savings,
      actual: savingsExpenses,
      difference: savings - savingsExpenses,
      status: savingsExpenses < savings ? 'under' : 'over'
    }
  ];

  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = [...tableData].sort((a, b) => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    switch (sortConfig.field) {
      case 'category':
        return direction * a.category.localeCompare(b.category);
      case 'allocated':
        return direction * (a.allocated - b.allocated);
      case 'actual':
        return direction * (a.actual - b.actual);
      case 'difference':
        return direction * (a.difference - b.difference);
      case 'status':
        return direction * a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  const filteredData = sortedData.filter(row =>
    row.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const ViewButton: React.FC<{ type: ViewType; label: string; icon: React.ReactNode }> = ({ type, label, icon }) => (
    <button
      onClick={() => setViewType(type)}
      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
        viewType === type 
          ? 'bg-emerald-500 text-white' 
          : 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  const SortButton: React.FC<{ field: SortField }> = ({ field }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 hover:text-emerald-500 transition-colors"
    >
      {field.charAt(0).toUpperCase() + field.slice(1)}
      <ArrowUpDown className={`w-4 h-4 ${sortConfig.field === field ? 'text-emerald-500' : 'text-slate-400'}`} />
    </button>
  );

  return (
    <div className="glass-card p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h3 className="text-xl font-semibold">Budget Overview</h3>
        <div className="flex flex-wrap gap-2">
          <ViewButton type="table" label="Table" icon={<TableIcon className="w-4 h-4" />} />
          <ViewButton type="bar" label="Bar" icon={<BarChart className="w-4 h-4" />} />
          <ViewButton type="pie" label="Pie" icon={<PieChart className="w-4 h-4" />} />
          <ViewButton type="line" label="Trend" icon={<LineChart className="w-4 h-4" />} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {viewType === 'table' ? (
          <motion.div
            key="table"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative w-full md:w-64">
                <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search categories..."
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                />
              </div>
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-400">Click column headers to sort</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-3"><SortButton field="category" /></th>
                    <th className="text-right p-3"><SortButton field="allocated" /></th>
                    <th className="text-right p-3"><SortButton field="actual" /></th>
                    <th className="text-right p-3"><SortButton field="difference" /></th>
                    <th className="text-right p-3"><SortButton field="status" /></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, index) => (
                    <tr key={index} className="border-b border-slate-800 hover:bg-slate-800/30">
                      <td className="p-3">{row.category}</td>
                      <td className="text-right p-3">${row.allocated.toLocaleString()}</td>
                      <td className="text-right p-3">${row.actual.toLocaleString()}</td>
                      <td className="text-right p-3">${Math.abs(row.difference).toLocaleString()}</td>
                      <td className="text-right p-3">
                        <span className={`inline-flex items-center gap-1 ${
                          row.status === 'over' 
                            ? row.category.includes('Savings') ? 'text-emerald-500' : 'text-red-500'
                            : row.category.includes('Savings') ? 'text-red-500' : 'text-emerald-500'
                        }`}>
                          {row.status === 'over' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                          {Math.abs(((row.actual - row.allocated) / row.allocated) * 100).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-700">
                    <td className="p-3 font-semibold">Total</td>
                    <td className="text-right p-3 font-semibold">${(needs + wants + savings).toLocaleString()}</td>
                    <td className="text-right p-3 font-semibold">${(needsExpenses + wantsExpenses + savingsExpenses).toLocaleString()}</td>
                    <td className="text-right p-3 font-semibold">
                      ${((needs + wants + savings) - (needsExpenses + wantsExpenses + savingsExpenses)).toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-8">
              <div className="glass-card p-4 border border-emerald-500/20">
                <h4 className="font-medium mb-4">Tax Distribution</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Federal Tax</span>
                    <span>{taxPercentages.federal.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>State Tax</span>
                    <span>{taxPercentages.state.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Social Security</span>
                    <span>{taxPercentages.socialSecurity.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Medicare</span>
                    <span>{taxPercentages.medicare.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-slate-700 pt-2">
                    <span>Take-Home Pay</span>
                    <span>{taxPercentages.takeHome.toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              <div className="glass-card p-4 border border-emerald-500/20">
                <h4 className="font-medium mb-4">Monthly Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Gross Income</span>
                    <span>${(annualIncome / 12).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Taxes</span>
                    <span>${((totalTaxes.federal + totalTaxes.state + totalTaxes.socialSecurity + totalTaxes.medicare) / 12).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Expenses</span>
                    <span>${(needsExpenses + wantsExpenses + savingsExpenses).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-slate-700 pt-2">
                    <span>Net Savings</span>
                    <span>${(monthlyTakeHome - (needsExpenses + wantsExpenses + savingsExpenses)).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={viewType}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <FinancialChart
              type={viewType === 'line' ? 'line' : viewType === 'pie' ? 'pie' : 'bar'}
              data={viewType === 'line' ? trendData : viewType === 'pie' ? pieData : budgetData}
              options={viewType === 'pie' ? chartOptions : undefined}
              height={400}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-sm text-slate-400">Needs</p>
          <p className="text-lg font-semibold text-emerald-500">
            ${needs.toLocaleString()}
          </p>
          <div className="flex items-center justify-center gap-1 text-xs">
            <span className={needsExpenses > needs ? 'text-red-500' : 'text-emerald-500'}>
              {needsExpenses > needs ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            </span>
            <span className="text-slate-400">
              ${needsExpenses.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="text-center">
          <p className="text-sm text-slate-400">Wants</p>
          <p className="text-lg font-semibold text-emerald-500">
            ${wants.toLocaleString()}
          </p>
          <div className="flex items-center justify-center gap-1 text-xs">
            <span className={wantsExpenses > wants ? 'text-red-500' : 'text-emerald-500'}>
              {wantsExpenses > wants ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            </span>
            <span className="text-slate-400">
              ${wantsExpenses.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="text-center">
          <p className="text-sm text-slate-400">Savings</p>
          <p className="text-lg font-semibold text-emerald-500">
            ${savings.toLocaleString()}
          </p>
          <div className="flex items-center justify-center gap-1 text-xs">
            <span className={savingsExpenses > savings ? 'text-emerald-500' : 'text-red-500'}>
              {savingsExpenses > savings ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            </span>
            <span className="text-slate-400">
              ${savingsExpenses.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};